import type { AllowanceSchedule, AppState, AssignmentSubmission, CooperativeMission, ChildPinChange, FamilyMissionChallenge, CollectionItem, ConsentRecord, ContentStudioEntry219, EconomicArticle, EncouragementMessage, ExperiencePlace, PassportVisit, LearningChapter, LearningScene, LoginCredentials, Mission, MissionTemplate, MoneyRecord, Notice, ParentRegistration, PasswordResetCompletion, PasswordResetRequest, QuizAttempt, QuizQuestion, QuizSuggestion, RewardPolicy, RewardRule, RewardTransaction, SavingGoal, ChildProfile, Role, TokenDispatchResult, User, VerificationCompletion } from '../domain/models';
import type { StateRepository } from './storageRepository';
import type { OperationsApi } from './operationsApi';
import { applySavingToGoal } from '../domain/rules';
import { isSessionActive } from '../domain/auth';
import { pinValidationMessage } from '../domain/pin';
import { completeBoardDay, updateGrowthSummary } from '../domain/growth';
import { completeChapterProgress } from '../domain/library';
import { recordReward, reviewFamilyMission as reviewMissionDomain, rewardTransactionForMission, submitFamilyMission as submitMissionDomain, validateFamilyMissionReward } from '../domain/familyRewards';
import { reviewPassportVisit as reviewPassportVisitDomain, stampForVisit, submitPassportVisit as submitPassportVisitDomain } from '../domain/passport';
import { defaultHomeJourneyProfile } from '../domain/homePersonalization';
import { contentStudioCanPublish219, contentStudioPrimaryRewardCount219 } from '../domain/contentStudio219';

const delay = (ms = 100) => new Promise((resolve) => setTimeout(resolve, ms));
const SESSION_HOURS = Number(import.meta.env.VITE_SESSION_HOURS ?? 8);

async function sha256(value: string): Promise<string> {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
}

function token(role: Role) { return `mock_${role}_${crypto.randomUUID()}`; }

function presentState(state: AppState): AppState {
  if (!['teacher','institution'].includes(state.session.role)) return state.session.role==='admin' ? state : { ...state, contentStudioEntries:[] };
  const teacherClassIds=state.classrooms.filter((item)=>item.teacherUserId===state.user.id&&item.active).map((item)=>item.id);
  const institutionOrgIds=state.schoolOrganizations.filter((item)=>item.managerUserIds.includes(state.user.id)&&item.status==='active').map((item)=>item.id);
  const institutionClassIds=state.classrooms.filter((item)=>institutionOrgIds.includes(item.organizationId)&&item.active).map((item)=>item.id);
  const classIds=state.session.role==='teacher'?teacherClassIds:institutionClassIds;
  const orgIds=state.session.role==='institution'?institutionOrgIds:[...new Set(state.classrooms.filter((item)=>classIds.includes(item.id)).map((item)=>item.organizationId))];
  const teams=state.cooperativeTeams.filter((item)=>Boolean(item.classroomId&&classIds.includes(item.classroomId)));
  const teamIds=teams.map((item)=>item.id);const missions=state.cooperativeMissions.filter((item)=>item.status==='published');const missionIds=missions.map((item)=>item.id);
  return {
    ...state,
    session:{...state.session,familyId:'',childId:''},
    users:[state.user],
    child:{id:'',familyId:'',nickname:'',birthYear:2014,pinEnabled:false},
    goal:{id:'',childId:'',title:'',targetAmount:1,currentAmount:0,weeklySavingAmount:0,targetDate:'',status:'active'},
    records:[],missions:[],quizAttempts:[],quizSuggestions:[],growthBoard:[],fridgeModels:[],growthSummary:{currentStreak:0,longestStreak:0,completedBoardDays:0,boardCycle:1},learningProgress:[],
    familyMissionChallenges:[],allowanceSchedules:[],rewardWallets:[],rewardTransactions:[],passportVisits:[],passportStamps:[],consents:[],messages:[],contentStudioEntries:[],homeJourney:defaultHomeJourneyProfile(''),diagnosis:null,badges:[],level:1,xp:0,
    schoolOrganizations:state.schoolOrganizations.filter((item)=>orgIds.includes(item.id)),
    classrooms:state.classrooms.filter((item)=>classIds.includes(item.id)),
    classroomMembers:state.classroomMembers.filter((item)=>classIds.includes(item.classroomId)),
    learningAssignments:state.learningAssignments.filter((item)=>classIds.includes(item.classroomId)),
    assignmentSubmissions:state.assignmentSubmissions.filter((item)=>classIds.includes(item.classroomId)),
    cooperativeTeams:teams,
    cooperativeMissions:missions,
    cooperativeClues:state.cooperativeClues.filter((item)=>missionIds.includes(item.missionId)),
    cooperativeProgress:state.cooperativeProgress.filter((item)=>teamIds.includes(item.teamId)&&missionIds.includes(item.missionId)),
    auditLog:state.session.role==='institution'?state.auditLog.filter((item)=>orgIds.includes(item.targetId)||classIds.includes(item.targetId)||teamIds.includes(item.targetId)):[]
  };
}
function expiresAt() { return new Date(Date.now() + SESSION_HOURS * 60 * 60 * 1000).toISOString(); }
function audit(state: AppState, action: string, targetType: string, targetId: string): AppState {
  return { ...state, auditLog: [{ id: `audit_${Date.now()}`, actorId: state.session.userId || 'anonymous', action, targetType, targetId, createdAt: new Date().toISOString() }, ...state.auditLog].slice(0, 200) };
}

export class MockApi implements OperationsApi {
  constructor(private readonly repository: StateRepository) {}

  async getState(): Promise<AppState> {
    await delay();
    const state = await this.repository.load();
    if (state.session.authenticated && !isSessionActive(state.session)) {
      const next = { ...state, session: { ...state.session, authenticated: false, accessToken: '', csrfToken: '', expiresAt: '' } };
      await this.repository.save(next);
      return presentState(next);
    }
    return presentState(state);
  }

  async login(credentials: LoginCredentials): Promise<AppState> {
    await delay(180);
    const state = await this.repository.load();
    const digest = await sha256(credentials.secret);
    const credential = state.mockCredentials.find((item) => item.role === credentials.role && item.identifier === credentials.identifier.trim() && item.secretDigest === digest);
    if (!credential) throw new Error('AUTH_INVALID');
    const user = state.users.find((item) => item.id === credential.userId && item.active);
    if (!user) throw new Error('AUTH_DISABLED');
    const schoolRole=['teacher','institution'].includes(credentials.role);
    const next = audit({ ...state, user, session: { ...state.session, authenticated: true, role: credentials.role, userId: user.id, familyId:schoolRole?'':state.child.familyId, childId:schoolRole?'':state.child.id, accessToken: token(credentials.role), csrfToken: token(credentials.role), expiresAt: expiresAt() } }, 'auth.login', 'user', user.id);
    await this.repository.save(next);
    return presentState(next);
  }

  async logout(): Promise<AppState> {
    await delay(60);
    const state = await this.repository.load();
    const next = audit({ ...state, session: { ...state.session, authenticated: false, accessToken: '', csrfToken: '', expiresAt: '' } }, 'auth.logout', 'user', state.session.userId);
    await this.repository.save(next);
    return presentState(next);
  }

  async registerParent(registration: ParentRegistration): Promise<AppState> {
    await delay(180);
    const state = await this.repository.load();
    if (registration.password.length < 8) throw new Error('PASSWORD_WEAK');
    if (state.users.some((user) => user.email.toLowerCase() === registration.email.toLowerCase())) throw new Error('EMAIL_EXISTS');
    const user: User = { id: `user_${crypto.randomUUID()}`, email: registration.email.trim(), name: registration.name.trim(), role: 'parent', active: true, emailVerified: false };
    const next = audit({
      ...state,
      users: [...state.users, user], mockCredentials: [...state.mockCredentials, { userId:user.id, identifier:user.email, role:'parent', secretDigest: await sha256(registration.password) }], user,
      session: { ...state.session, authenticated: true, role: 'parent', userId: user.id, accessToken: token('parent'), csrfToken: token('parent'), expiresAt: expiresAt() }
    }, 'auth.register', 'user', user.id);
    await this.repository.save(next);
    return next;
  }


  async requestEmailVerification(): Promise<TokenDispatchResult> {
    await delay();
    return { accepted:true, devToken:`mock_verify_${crypto.randomUUID()}` };
  }

  async verifyEmail(_input: VerificationCompletion): Promise<TokenDispatchResult> {
    await delay();
    const state = await this.repository.load();
    const next = { ...state, user:{ ...state.user, emailVerified:true }, users:state.users.map((item)=>item.id===state.user.id?{...item,emailVerified:true}:item) };
    await this.repository.save(next);
    return { verified:true };
  }

  async requestPasswordReset(_input: PasswordResetRequest): Promise<TokenDispatchResult> {
    await delay();
    return { accepted:true, devToken:`mock_reset_${crypto.randomUUID()}` };
  }

  async resetPassword(_input: PasswordResetCompletion): Promise<TokenDispatchResult> {
    await delay();
    return { reset:true };
  }

  async recordConsent(consent: ConsentRecord): Promise<AppState> {
    await delay();
    const state = await this.repository.load();
    const consents = [consent, ...state.consents.filter((item) => item.userId !== consent.userId)];
    const next = audit({ ...state, consents }, 'consent.accept', 'consent', consent.id);
    await this.repository.save(next);
    return next;
  }

  async updateChild(child: ChildProfile): Promise<AppState> {
    return this.persist('child.update', 'child', child.id, (s) => ({
      ...s, child,
      users: s.users.map((user) => user.role === 'child' ? { ...user, name: child.nickname } : user),
      mockCredentials: s.mockCredentials.map((item) => item.role === 'child' ? { ...item, identifier: child.nickname } : item)
    }));
  }

  async updateChildPin(change: ChildPinChange): Promise<AppState> {
    const validation = pinValidationMessage(change.newPin);
    if (validation) throw new Error('PIN_WEAK');
    const state = await this.repository.load();
    if (!state.session.authenticated || state.session.role !== 'parent') throw new Error('AUTH_FORBIDDEN');
    const childUser = state.users.find((user) => user.role === 'child');
    if (!childUser || change.childId !== state.child.id) throw new Error('CHILD_NOT_FOUND');
    const digest = await sha256(change.newPin);
    const next = audit({
      ...state,
      child: { ...state.child, pinEnabled: true, pinUpdatedAt: new Date().toISOString() },
      mockCredentials: state.mockCredentials.map((item) => item.userId === childUser.id ? { ...item, secretDigest: digest, identifier: state.child.nickname } : item)
    }, 'child.pin.update', 'child', change.childId);
    await this.repository.save(next);
    return next;
  }
  async updateGoal(goal: SavingGoal): Promise<AppState> { return this.persist('goal.update', 'goal', goal.id, (s) => ({ ...s, goal })); }
  async addRecord(record: MoneyRecord): Promise<AppState> { return this.persist('record.create', 'record', record.id, (s) => ({ ...s, records: [record, ...s.records], goal: applySavingToGoal(s.goal, record) })); }
  async updateMission(mission: Mission): Promise<AppState> { return this.persist('mission.update', 'mission', mission.id, (s) => ({ ...s, missions: s.missions.map((item) => item.id === mission.id ? mission : item) })); }
  async updateRewardRule(rewardRule: RewardRule): Promise<AppState> { return this.persist('reward.update', 'rewardRule', 'family', (s) => ({ ...s, rewardRule })); }
  async sendEncouragement(message: EncouragementMessage): Promise<AppState> {
    return this.persist('message.create', 'message', message.id, (s) => ({ ...s, messages: [message, ...s.messages].slice(0, 200) }));
  }

  async patchState(patch: Partial<AppState>): Promise<AppState> {
    return this.persist('state.patch', 'appState', 'current', (s) => ({ ...s, ...patch }));
  }

  async setRole(role: AppState['session']['role']): Promise<AppState> {
    const state = await this.repository.load();
    const user = state.users.find((item) => item.role === role && item.active) ?? state.user;
    const schoolRole=['teacher','institution'].includes(role);
    const next = audit({ ...state, user, session: { ...state.session, authenticated: true, role, userId: user.id, familyId:schoolRole?'':state.child.familyId, childId:schoolRole?'':state.child.id, accessToken: token(role), csrfToken: token(role), expiresAt: expiresAt() } }, 'demo.role-switch', 'role', role);
    await this.repository.save(next);
    return presentState(next);
  }

  async upsertMissionTemplate(template: MissionTemplate): Promise<AppState> {
    return this.persist('admin.mission-template.upsert', 'missionTemplate', template.id, (s) => ({ ...s, missionTemplates: s.missionTemplates.some((item) => item.id === template.id) ? s.missionTemplates.map((item) => item.id === template.id ? template : item) : [template, ...s.missionTemplates] }));
  }

  async deleteMissionTemplate(templateId: string): Promise<AppState> {
    return this.persist('admin.mission-template.delete', 'missionTemplate', templateId, (s) => ({ ...s, missionTemplates: s.missionTemplates.filter((item) => item.id !== templateId) }));
  }

  async upsertNotice(notice: Notice): Promise<AppState> {
    return this.persist('admin.notice.upsert', 'notice', notice.id, (s) => ({ ...s, notices: s.notices.some((item) => item.id === notice.id) ? s.notices.map((item) => item.id === notice.id ? notice : item) : [notice, ...s.notices] }));
  }

  async deleteNotice(noticeId: string): Promise<AppState> {
    return this.persist('admin.notice.delete', 'notice', noticeId, (s) => ({ ...s, notices: s.notices.filter((item) => item.id !== noticeId) }));
  }

  async submitQuizAttempt(attempt: QuizAttempt): Promise<AppState> {
    return this.persist('quiz.attempt.submit', 'quizAttempt', attempt.id, (s) => {
      if (s.quizAttempts.some((item) => item.id === attempt.id)) return s;
      const badges = attempt.score === 100 && !s.badges.includes('경제기사 만점') ? [...s.badges, '경제기사 만점'] : s.badges;
      const completedAt = attempt.completedAt;
      const availableQuizDay = s.growthBoard.find((item) => item.status === 'available' && item.activityType === 'quiz');
      const growthBoard = availableQuizDay ? completeBoardDay(s.growthBoard, availableQuizDay.id, completedAt) : s.growthBoard;
      const growthSummary = availableQuizDay ? updateGrowthSummary(s.growthSummary, completedAt) : s.growthSummary;
      const collectionItems = s.collectionItems.map((item) => item.id === 'collect_quiz_star' && !item.unlockedAt ? { ...item, unlockedAt: completedAt } : item);
      const fridgeModels = s.fridgeModels.map((fridge) => fridge.collectionItemIds.includes('collect_quiz_star') ? fridge : { ...fridge, unlockedSlots: Math.min(fridge.totalSlots, fridge.unlockedSlots + 1), collectionItemIds: [...fridge.collectionItemIds, 'collect_quiz_star'] });
      return { ...s, quizAttempts: [attempt, ...s.quizAttempts], xp: s.xp + attempt.xpEarned + (availableQuizDay?.rewardXp ?? 0), badges, growthBoard, growthSummary, collectionItems, fridgeModels };
    });
  }

  async submitQuizSuggestion(suggestion: QuizSuggestion): Promise<AppState> {
    return this.persist('quiz.suggestion.submit', 'quizSuggestion', suggestion.id, (s) => ({ ...s, quizSuggestions: [suggestion, ...s.quizSuggestions] }));
  }


  async completeGrowthBoardDay(dayId: string): Promise<AppState> {
    return this.persist('growth-board.complete', 'growthBoardDay', dayId, (s) => {
      const day = s.growthBoard.find((item) => item.id === dayId);
      if (!day || day.status === 'completed') return s;
      const completedAt = new Date().toISOString();
      const growthBoard = completeBoardDay(s.growthBoard, dayId, completedAt);
      const changed = growthBoard.some((item, index) => item.status !== s.growthBoard[index]?.status);
      if (!changed) return s;
      const growthSummary = updateGrowthSummary(s.growthSummary, completedAt);
      const xp = s.xp + day.rewardXp;
      const collectionItems = s.collectionItems.map((item) => item.id === 'collect_quiz_star' && day.activityType === 'quiz' && !item.unlockedAt ? { ...item, unlockedAt: completedAt } : item);
      const fridgeModels = s.fridgeModels.map((fridge) => {
        const itemUnlocked = day.activityType === 'quiz' && !fridge.collectionItemIds.includes('collect_quiz_star');
        return itemUnlocked ? { ...fridge, unlockedSlots: Math.min(fridge.totalSlots, fridge.unlockedSlots + 1), collectionItemIds: [...fridge.collectionItemIds, 'collect_quiz_star'] } : fridge;
      });
      const badges = growthSummary.completedBoardDays >= 30 && !s.badges.includes('30일 성장 완주') ? [...s.badges, '30일 성장 완주'] : s.badges;
      return { ...s, growthBoard, growthSummary, xp, collectionItems, fridgeModels, badges };
    });
  }

  async upsertCollectionItem(item: CollectionItem): Promise<AppState> {
    return this.persist('admin.collection-item.upsert', 'collectionItem', item.id, (s) => ({
      ...s,
      collectionItems: s.collectionItems.some((existing) => existing.id === item.id)
        ? s.collectionItems.map((existing) => existing.id === item.id ? item : existing)
        : [item, ...s.collectionItems]
    }));
  }

  async upsertEconomicArticle(article: EconomicArticle): Promise<AppState> {
    return this.persist('admin.economic-article.upsert', 'economicArticle', article.id, (s) => ({ ...s, economicArticles: s.economicArticles.some((item) => item.id === article.id) ? s.economicArticles.map((item) => item.id === article.id ? article : item) : [article, ...s.economicArticles] }));
  }

  async deleteEconomicArticle(articleId: string): Promise<AppState> {
    return this.persist('admin.economic-article.delete', 'economicArticle', articleId, (s) => ({ ...s, economicArticles: s.economicArticles.filter((item) => item.id !== articleId), quizQuestions: s.quizQuestions.filter((item) => item.articleId !== articleId) }));
  }

  async upsertQuizQuestion(question: QuizQuestion): Promise<AppState> {
    return this.persist('admin.quiz-question.upsert', 'quizQuestion', question.id, (s) => ({ ...s, quizQuestions: s.quizQuestions.some((item) => item.id === question.id) ? s.quizQuestions.map((item) => item.id === question.id ? question : item) : [question, ...s.quizQuestions] }));
  }

  async deleteQuizQuestion(questionId: string): Promise<AppState> {
    return this.persist('admin.quiz-question.delete', 'quizQuestion', questionId, (s) => ({ ...s, quizQuestions: s.quizQuestions.filter((item) => item.id !== questionId) }));
  }

  async upsertContentStudioEntry(entry: ContentStudioEntry219): Promise<AppState> {
    return this.persist('admin.content-studio.upsert','contentStudioEntry',entry.id,(s)=>{
      if(s.session.role!=='admin') throw new Error('AUTH_FORBIDDEN');
      const article=s.economicArticles.find((item)=>item.id===entry.articleId);if(!article)throw new Error('VALIDATION_ERROR');
      if(contentStudioPrimaryRewardCount219(entry)>2)throw new Error('REWARD_PLAN_OVERLOADED');
      if(['approved','scheduled','published'].includes(entry.workflowStatus)&&!contentStudioCanPublish219(entry))throw new Error('CONTENT_REVIEW_INCOMPLETE');
      const now=new Date().toISOString();const item={...entry,reviewedBy:['approved','scheduled','published'].includes(entry.workflowStatus)?s.user.name:entry.reviewedBy,reviewedAt:['approved','scheduled','published'].includes(entry.workflowStatus)?now:entry.reviewedAt,updatedAt:now};
      const basic=item.variants.find((variant)=>variant.difficulty===2)??item.variants[0];
      const articleStatus=item.workflowStatus==='approved'?'review':item.workflowStatus==='candidate'?'draft':item.workflowStatus;
      const economicArticles=s.economicArticles.map((candidate)=>candidate.id===article.id?{...candidate,title:basic.title,summary:basic.explanation.join(' '),childSummary:basic.summary,keyPoints:basic.explanation,difficulty:2 as const,status:articleStatus,scheduledAt:item.targetDate?new Date(`${item.targetDate.length===16?item.targetDate+':00':item.targetDate}+09:00`).toISOString():candidate.scheduledAt,publishedAt:item.workflowStatus==='published'?(candidate.publishedAt??now):candidate.publishedAt,reviewedBy:item.reviewedBy,updatedAt:now}:candidate);
      const existingQuiz=s.quizQuestions.find((candidate)=>candidate.articleId===article.id);const quizId=existingQuiz?.id??`quiz_studio_${article.id}`;
      const choices=basic.quizChoices.map((label,index)=>({id:`${quizId}_choice_${index+1}`,label}));
      const quiz={...(existingQuiz??{}),id:quizId,articleId:article.id,type:'single' as const,prompt:basic.quizPrompt,choices,correctChoiceId:choices[Math.min(basic.correctChoiceIndex,choices.length-1)]?.id??'',explanation:basic.quizExplanation,conceptId:existingQuiz?.conceptId??article.conceptIds[0]??'concept_price',order:existingQuiz?.order??1,active:item.workflowStatus==='published'||item.workflowStatus==='scheduled'};
      const quizQuestions=existingQuiz?s.quizQuestions.map((candidate)=>candidate.id===existingQuiz.id?quiz:candidate):[quiz,...s.quizQuestions];
      return {...s,economicArticles,quizQuestions,contentStudioEntries:s.contentStudioEntries.some((candidate)=>candidate.id===item.id)?s.contentStudioEntries.map((candidate)=>candidate.id===item.id?item:candidate):[item,...s.contentStudioEntries]};
    });
  }

  async completeLearningChapter(chapterId: string): Promise<AppState> {
    return this.persist('learning.chapter.complete', 'learningChapter', chapterId, (s) => {
      const chapter = s.learningChapters.find((item) => item.id === chapterId && item.status === 'published');
      if (!chapter) throw new Error('VALIDATION_ERROR');
      const existing = s.learningProgress.find((item) => item.childId === s.child.id && item.chapterId === chapterId);
      if (existing?.completed) return s;
      const progress = completeChapterProgress({ chapter, childId:s.child.id, existing });
      const learningProgress = [progress, ...s.learningProgress.filter((item)=>item.id!==progress.id)];
      const badges = chapter.badgeReward && !s.badges.includes(chapter.badgeReward) ? [...s.badges, chapter.badgeReward] : s.badges;
      return { ...s, learningProgress, badges, xp:s.xp+chapter.xpReward };
    });
  }

  async upsertLearningChapter(chapter: LearningChapter): Promise<AppState> {
    return this.persist('admin.learning-chapter.upsert', 'learningChapter', chapter.id, (s) => ({
      ...s, learningChapters:s.learningChapters.some((item)=>item.id===chapter.id)?s.learningChapters.map((item)=>item.id===chapter.id?chapter:item):[chapter,...s.learningChapters]
    }));
  }

  async upsertLearningScene(scene: LearningScene): Promise<AppState> {
    return this.persist('admin.learning-scene.upsert', 'learningScene', scene.id, (s) => ({
      ...s, learningScenes:s.learningScenes.some((item)=>item.id===scene.id)?s.learningScenes.map((item)=>item.id===scene.id?scene:item):[scene,...s.learningScenes],
      learningChapters:s.learningChapters.map((chapter)=>chapter.id===scene.chapterId&&!chapter.sceneIds.includes(scene.id)?{...chapter,sceneIds:[...chapter.sceneIds,scene.id]}:chapter)
    }));
  }

  async deleteLearningScene(sceneId: string): Promise<AppState> {
    return this.persist('admin.learning-scene.delete', 'learningScene', sceneId, (s) => ({
      ...s, learningScenes:s.learningScenes.filter((item)=>item.id!==sceneId), learningChapters:s.learningChapters.map((chapter)=>({...chapter,sceneIds:chapter.sceneIds.filter((id)=>id!==sceneId)}))
    }));
  }

  async reviewQuizSuggestion(suggestionId: string, status: QuizSuggestion['status'], rewardXp: number): Promise<AppState> {
    return this.persist('admin.quiz-suggestion.review', 'quizSuggestion', suggestionId, (s) => {
      const target=s.quizSuggestions.find((item)=>item.id===suggestionId);
      if(!target) throw new Error('VALIDATION_ERROR');
      const previousAccepted=target.status==='accepted';
      const nowAccepted=status==='accepted';
      return { ...s, quizSuggestions:s.quizSuggestions.map((item)=>item.id===suggestionId?{...item,status,rewardXp,reviewedAt:new Date().toISOString()}:item), xp:s.xp+(!previousAccepted&&nowAccepted?rewardXp:0) };
    });
  }

  async upsertFamilyMission(mission: FamilyMissionChallenge): Promise<AppState> {
    return this.persist('family-mission.upsert','familyMission',mission.id,(s)=>{
      if(s.session.role!=='parent'&&s.session.role!=='admin') throw new Error('AUTH_FORBIDDEN');
      const policy=s.rewardPolicies[0];
      if(policy){const issue=validateFamilyMissionReward(mission.reward.amount,mission.reward.type,policy);if(issue)throw new Error('VALIDATION_ERROR');}
      const item={...mission,familyId:s.child.familyId,childId:s.child.id,updatedAt:new Date().toISOString()};
      return {...s,familyMissionChallenges:s.familyMissionChallenges.some((x)=>x.id===item.id)?s.familyMissionChallenges.map((x)=>x.id===item.id?item:x):[item,...s.familyMissionChallenges]};
    });
  }

  async submitFamilyMission(missionId: string, note: string): Promise<AppState> {
    return this.persist('family-mission.submit','familyMission',missionId,(s)=>{
      if(s.session.role!=='child')throw new Error('AUTH_FORBIDDEN');
      const target=s.familyMissionChallenges.find((x)=>x.id===missionId&&x.childId===s.child.id);if(!target)throw new Error('VALIDATION_ERROR');
      const next=submitMissionDomain(target,note);
      return {...s,familyMissionChallenges:s.familyMissionChallenges.map((x)=>x.id===missionId?next:x)};
    });
  }

  async reviewFamilyMission(missionId: string, decision: 'approved' | 'held'): Promise<AppState> {
    return this.persist('family-mission.review','familyMission',missionId,(s)=>{
      if(s.session.role!=='parent')throw new Error('AUTH_FORBIDDEN');
      const target=s.familyMissionChallenges.find((x)=>x.id===missionId&&x.familyId===s.child.familyId);if(!target)throw new Error('VALIDATION_ERROR');
      const reviewed=reviewMissionDomain(target,decision);
      let rewardTransactions=s.rewardTransactions;
      if(decision==='approved'&&!rewardTransactions.some((x)=>x.missionId===missionId)) rewardTransactions=[rewardTransactionForMission(reviewed),...rewardTransactions];
      return {...s,familyMissionChallenges:s.familyMissionChallenges.map((x)=>x.id===missionId?reviewed:x),rewardTransactions};
    });
  }

  async updateAllowanceSchedule(schedule: AllowanceSchedule): Promise<AppState> {
    return this.persist('allowance-schedule.update','allowanceSchedule',schedule.id,(s)=>{
      if(s.session.role!=='parent')throw new Error('AUTH_FORBIDDEN');
      const item={...schedule,familyId:s.child.familyId,childId:s.child.id,updatedAt:new Date().toISOString()};
      return {...s,allowanceSchedules:s.allowanceSchedules.some((x)=>x.id===item.id)?s.allowanceSchedules.map((x)=>x.id===item.id?item:x):[item,...s.allowanceSchedules]};
    });
  }

  async recordRewardTransaction(transactionId: string): Promise<AppState> {
    return this.persist('reward-transaction.record','rewardTransaction',transactionId,(s)=>{
      if(s.session.role!=='parent')throw new Error('AUTH_FORBIDDEN');
      const tx=s.rewardTransactions.find((x)=>x.id===transactionId&&x.familyId===s.child.familyId);if(!tx)throw new Error('VALIDATION_ERROR');
      const wallet=s.rewardWallets.find((x)=>x.childId===tx.childId)??{childId:tx.childId,allowanceBalance:0,savingBalance:0,simulatedInvestmentBalance:0,updatedAt:new Date().toISOString()};
      const recorded=recordReward(wallet,tx);
      return {...s,rewardWallets:[recorded.wallet,...s.rewardWallets.filter((x)=>x.childId!==tx.childId)],rewardTransactions:s.rewardTransactions.map((x)=>x.id===tx.id?recorded.transaction:x),familyMissionChallenges:s.familyMissionChallenges.map((x)=>x.id===tx.missionId?{...x,status:'completed',updatedAt:new Date().toISOString()}:x)};
    });
  }

  async upsertRewardPolicy(policy: RewardPolicy): Promise<AppState> {
    return this.persist('admin.reward-policy.upsert','rewardPolicy',policy.id,(s)=>{if(s.session.role!=='admin')throw new Error('AUTH_FORBIDDEN');const item={...policy,actualTransferEnabled:false,updatedAt:new Date().toISOString()};return {...s,rewardPolicies:s.rewardPolicies.some((x)=>x.id===item.id)?s.rewardPolicies.map((x)=>x.id===item.id?item:x):[item,...s.rewardPolicies]};});
  }

  async upsertExperiencePlace(place: ExperiencePlace): Promise<AppState> {
    return this.persist('admin.experience-place.upsert','experiencePlace',place.id,(s)=>{
      if(s.session.role!=='admin') throw new Error('AUTH_FORBIDDEN');
      const item={...place,updatedAt:new Date().toISOString()};
      return {...s,experiencePlaces:s.experiencePlaces.some((x)=>x.id===item.id)?s.experiencePlaces.map((x)=>x.id===item.id?item:x):[item,...s.experiencePlaces]};
    });
  }

  async submitPassportVisit(visit: PassportVisit): Promise<AppState> {
    return this.persist('passport-visit.submit','passportVisit',visit.id,(s)=>{
      if(s.session.role!=='child') throw new Error('AUTH_FORBIDDEN');
      const place=s.experiencePlaces.find((x)=>x.id===visit.placeId&&x.status==='published'); if(!place) throw new Error('VALIDATION_ERROR');
      const item=submitPassportVisitDomain({...visit,familyId:s.child.familyId,childId:s.child.id},place);
      return {...s,passportVisits:s.passportVisits.some((x)=>x.id===item.id)?s.passportVisits.map((x)=>x.id===item.id?item:x):[item,...s.passportVisits]};
    });
  }

  async reviewPassportVisit(visitId: string, decision: 'approved' | 'held' | 'rejected', parentNote: string): Promise<AppState> {
    return this.persist('passport-visit.review','passportVisit',visitId,(s)=>{
      if(s.session.role!=='parent') throw new Error('AUTH_FORBIDDEN');
      const visit=s.passportVisits.find((x)=>x.id===visitId&&x.familyId===s.child.familyId); if(!visit) throw new Error('VALIDATION_ERROR');
      const reviewed=reviewPassportVisitDomain(visit,decision,parentNote);
      let passportStamps=s.passportStamps;
      if(decision==='approved'&&!passportStamps.some((x)=>x.id===`stamp_${visit.id}`)){
        const place=s.experiencePlaces.find((x)=>x.id===visit.placeId); if(place) passportStamps=[stampForVisit(reviewed,place),...passportStamps];
      }
      return {...s,passportVisits:s.passportVisits.map((x)=>x.id===visitId?reviewed:x),passportStamps,xp:s.xp+(decision==='approved'?80:0)};
    });
  }

  async submitAssignment(submission: AssignmentSubmission): Promise<AppState> {
    return this.persist('school.assignment.submit','assignmentSubmission',submission.id,(s)=>{
      if(s.session.role!=='child') throw new Error('AUTH_FORBIDDEN');
      const assignment=s.learningAssignments.find((x)=>x.id===submission.assignmentId&&x.status!=='draft'&&x.status!=='closed');
      if(!assignment) throw new Error('ASSIGNMENT_NOT_FOUND');
      const member=s.classroomMembers.find((x)=>x.classroomId===assignment.classroomId&&x.childId===s.child.id&&x.status==='active');
      if(!member) throw new Error('AUTH_FORBIDDEN');
      const existing=s.assignmentSubmissions.find((x)=>x.id===submission.id);
      if(existing&&(existing.childId!==s.child.id||existing.assignmentId!==assignment.id||existing.classroomId!==assignment.classroomId)) throw new Error('AUTH_FORBIDDEN');
      const assignmentSubmission=s.assignmentSubmissions.find((x)=>x.assignmentId===assignment.id&&x.childId===s.child.id);
      if(assignmentSubmission&&assignmentSubmission.id!==submission.id) throw new Error('SUBMISSION_ALREADY_EXISTS');
      if(existing?.status==='approved') throw new Error('SUBMISSION_ALREADY_APPROVED');
      const note=String(submission.note||'').trim();if(note.length<2||note.length>2000) throw new Error('VALIDATION_ERROR');
      const item: AssignmentSubmission={id:submission.id,assignmentId:assignment.id,classroomId:assignment.classroomId,childId:s.child.id,status:'submitted',note,submittedAt:new Date().toISOString()};
      return {...s,assignmentSubmissions:s.assignmentSubmissions.some((x)=>x.id===item.id)?s.assignmentSubmissions.map((x)=>x.id===item.id?item:x):[item,...s.assignmentSubmissions]};
    });
  }

  async reviewAssignmentSubmission(submissionId: string, decision: 'approved' | 'revision', feedback: string): Promise<AppState> {
    return this.persist('school.assignment.review','assignmentSubmission',submissionId,(s)=>{
      if(!['teacher','admin'].includes(s.session.role)) throw new Error('AUTH_FORBIDDEN');
      const target=s.assignmentSubmissions.find((x)=>x.id===submissionId);if(!target) throw new Error('NOT_FOUND');
      const classroom=s.classrooms.find((x)=>x.id===target.classroomId);if(!classroom) throw new Error('CLASSROOM_NOT_FOUND');
      if(s.session.role!=='admin'&&classroom.teacherUserId!==s.user.id) throw new Error('AUTH_FORBIDDEN');
      const cleanFeedback=String(feedback||'').trim();if(cleanFeedback.length>2000) throw new Error('VALIDATION_ERROR');
      return {...s,assignmentSubmissions:s.assignmentSubmissions.map((x)=>x.id===submissionId?{...x,status:decision,feedback:cleanFeedback,reviewedAt:new Date().toISOString()}:x)};
    });
  }

  async completeCooperativeClue(progressId: string, clueId: string): Promise<AppState> {
    return this.persist('cooperative.clue.complete','cooperativeProgress',progressId,(s)=>{
      if(s.session.role!=='child') throw new Error('AUTH_FORBIDDEN');
      const progress=s.cooperativeProgress.find((x)=>x.id===progressId);if(!progress) throw new Error('NOT_FOUND');
      const team=s.cooperativeTeams.find((x)=>x.id===progress.teamId);if(!team||!team.memberChildIds.includes(s.child.id)) throw new Error('AUTH_FORBIDDEN');
      const mission=s.cooperativeMissions.find((x)=>x.id===progress.missionId&&x.status==='published');if(!mission) throw new Error('NOT_FOUND');
      const clue=s.cooperativeClues.find((x)=>x.id===clueId&&x.missionId===mission.id&&mission.clueIds.includes(x.id));if(!clue) throw new Error('CLUE_NOT_FOUND');
      if(progress.completedClueIds.includes(clue.id)) return s;
      const ordered=s.cooperativeClues.filter((x)=>x.missionId===mission.id&&mission.clueIds.includes(x.id)).sort((a,b)=>a.order-b.order);
      const clueIndex=ordered.findIndex((x)=>x.id===clue.id);if(clueIndex>0&&!progress.completedClueIds.includes(ordered[clueIndex-1].id)) throw new Error('CLUE_SEQUENCE_CONFLICT');
      const completed=[...progress.completedClueIds,clue.id];const done=mission.clueIds.every((id)=>completed.includes(id));
      const previousXp=Math.max(0,Number(progress.xpEarned||0));const nextXp=done?Math.max(0,mission.xpReward):Math.min(Math.max(0,mission.xpReward),completed.length*30);const delta=Math.max(0,nextXp-previousXp);
      return {...s,cooperativeProgress:s.cooperativeProgress.map((x)=>x.id===progressId?{...x,completedClueIds:completed,status:done?'completed':'active',completedAt:done?(x.completedAt??new Date().toISOString()):x.completedAt,xpEarned:nextXp}:x),xp:s.xp+delta};
    });
  }

  async upsertCooperativeMission(mission: CooperativeMission): Promise<AppState> {
    return this.persist('admin.cooperative-mission.upsert','cooperativeMission',mission.id,(s)=>{
      if(s.session.role!=='admin') throw new Error('AUTH_FORBIDDEN');
      if(!mission.title.trim()||!mission.description.trim()||mission.clueIds.length<1||mission.recommendedMembers<2||mission.recommendedMembers>5||mission.xpReward<0||mission.xpReward>10000) throw new Error('VALIDATION_ERROR');
      if(mission.status==='published'&&mission.clueIds.some((clueId)=>!s.cooperativeClues.some((clue)=>clue.id===clueId&&clue.missionId===mission.id))) throw new Error('MISSION_CLUE_VALIDATION_FAILED');
      const item={...mission,clueIds:[...new Set(mission.clueIds)],updatedAt:new Date().toISOString()};
      return {...s,cooperativeMissions:s.cooperativeMissions.some((x)=>x.id===item.id)?s.cooperativeMissions.map((x)=>x.id===item.id?item:x):[item,...s.cooperativeMissions]};
    });
  }

  async reset(): Promise<AppState> { await delay(80); return this.repository.reset(); }

  private async persist(action: string, targetType: string, targetId: string, mutate: (state: AppState) => AppState): Promise<AppState> {
    await delay();
    const state = await this.repository.load();
    const next = audit(mutate(state), action, targetType, targetId);
    await this.repository.save(next);
    return presentState(next);
  }
}
